import { Card } from '../components/ui/card';
import { LoadingSpinner } from '../components/ui/loading-states';
import { QueryError } from '../components/ui/error-state';
import { useHealth, useEmployees, useDepartments, useLocations, useAttendanceEvents } from '../hooks/useApi';
import { useAuth } from '../providers/AuthProvider';
import { usePermissions } from '../hooks/usePermissions';
import { MetricCard } from '../components/dashboard/MetricCard';
import { RecentCheckInsTable } from '../components/dashboard/RecentCheckInsTable';
import { Users, Building2, MapPin, TrendingUp } from 'lucide-react';

export function Dashboard() {
  const { isAuthenticated } = useAuth();
  const { can } = usePermissions();
  const canViewAttendance = can('attendance', 'view');
  const healthQuery = useHealth();
  const employeesQuery = useEmployees({ limit: 1 }, { enabled: isAuthenticated }); // Just for count
  const departmentsQuery = useDepartments({ limit: 1 }, { enabled: isAuthenticated }); // Just for count
  const locationsQuery = useLocations({ limit: 1 }, { enabled: isAuthenticated }); // Just for count
  
  // Получаем последние 5 событий прихода
  const recentCheckInsQuery = useAttendanceEvents(
    {
      limit: 5,
      sort: 'DESC',
      eventType: 'check_in'
    },
    { enabled: isAuthenticated && canViewAttendance }
  );
  
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 gradient-text-blue">Панель управления</h1>
          <p className="text-muted-foreground mt-2 text-body-lg">
            Добро пожаловать в TCO Панель администратора v2.0
          </p>
        </div>

        {/* Health status indicator */}
        <div className="flex items-center gap-2">
          {healthQuery.isLoading && <LoadingSpinner size="sm" />}
          {healthQuery.isSuccess && (
            <div className="flex items-center gap-2 px-4 py-2 bg-success-light rounded-xl">
              <div className="w-2.5 h-2.5 bg-success rounded-full animate-pulse" />
              <span className="text-sm font-medium text-success-dark">API подключено</span>
            </div>
          )}
          {healthQuery.isError && (
            <div className="flex items-center gap-2 px-4 py-2 bg-error-light rounded-xl">
              <div className="w-2.5 h-2.5 bg-error rounded-full" />
              <span className="text-sm font-medium text-error-dark">API не доступно</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Employees Card */}
        <MetricCard
          title="Всего сотрудников"
          value={employeesQuery.data?.total || 0}
          icon={Users}
          trend={{ value: 12, isPositive: true }}
          iconColor="text-tco-blue"
          iconBgColor="bg-tco-blue-100"
        />

        {/* Active Today Card */}
        <MetricCard
          title="Активны сегодня"
          value={employeesQuery.data?.total 
            ? Math.floor(employeesQuery.data.total * 0.85) 
            : 0
          }
          icon={TrendingUp}
          trend={{ value: 8, isPositive: true }}
          iconColor="text-success"
          iconBgColor="bg-success-light"
        />

        {/* Locations Card */}
        <MetricCard
          title="Локации"
          value={locationsQuery.data?.total || 0}
          icon={MapPin}
          trend={{ value: 5, isPositive: true }}
          iconColor="text-info"
          iconBgColor="bg-info-light"
        />

        {/* Departments Card */}
        <MetricCard
          title="Отделы"
          value={departmentsQuery.data?.total || 0}
          icon={Building2}
          iconColor="text-warning"
          iconBgColor="bg-warning-light"
        />
      </div>

      {/* API Status Section */}
      <Card className="p-8">
        <h3 className="text-h3 mb-6">Статус системы</h3>
        <div className="space-y-4">
          {healthQuery.isError ? (
            <QueryError
              error={healthQuery.error}
              refetch={healthQuery.refetch}
              className="p-4"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-gradient-to-br from-tco-blue-50 to-white rounded-xl border border-tco-blue-100 hover-lift">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-tco-blue-100 rounded-lg flex items-center justify-center">
                    <div className="w-3 h-3 bg-tco-blue rounded-full" />
                  </div>
                  <h4 className="font-semibold text-foreground">TanStack Query</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  Статус: <span className="text-success font-semibold">Активен</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Версия: <span className="font-medium">5.90.2</span>
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-success-light to-white rounded-xl border border-success/20 hover-lift">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-success-light rounded-lg flex items-center justify-center">
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                  </div>
                  <h4 className="font-semibold text-foreground">Соединение с API</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  Статус: <span className={healthQuery.isSuccess ? "text-success font-semibold" : "text-error font-semibold"}>
                    {healthQuery.isSuccess ? "Подключено" : "Отключено"}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Точка: <span className="font-medium">api.weekapp.kz</span>
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-info-light to-white rounded-xl border border-info/20 hover-lift">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-info-light rounded-lg flex items-center justify-center">
                    <div className="w-3 h-3 bg-info rounded-full" />
                  </div>
                  <h4 className="font-semibold text-foreground">Кэширование данных</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  Стратегия: <span className="text-info font-semibold">Оптимистичная</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Время: <span className="font-medium">5 минут</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Recent Check-ins — только при доступе к разделу «Посещаемость» */}
      {canViewAttendance && (
      <Card className="p-6">
        <h3 className="text-h3 mb-6">Последние отметки</h3>
        {recentCheckInsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : recentCheckInsQuery.isError ? (
          <QueryError
            error={recentCheckInsQuery.error}
            refetch={recentCheckInsQuery.refetch}
            className="p-4"
          />
        ) : (
          <RecentCheckInsTable
            events={recentCheckInsQuery.data?.data || []}
          />
        )}
      </Card>
      )}
    </div>
  );
}